// 画像以外のファイル（.pdfや.xlsx）のプレビュー
import IconDownload from '@src/assets/images/common/icon/chat-action/icon-download.svg';
import { useSharedAuth } from '@src/context/auth/useSharedAuth';
import { useIsModal } from '@src/context/IsModalContext';
import { fetchIcon } from '@src/enum/chatAttachableFileExtension';
import AttachmentActionMenuSp from '@src/features/shared/community/components/chat/AttachmentActionMenuSp';
import AttachmentErrorModal from '@src/features/shared/community/components/chat/chatList/AttachmentErrorModal';
import { ChatAttachmentItemProps } from '@src/features/shared/community/components/chat/chatList/ChatAttachmentList';
import { TOOLTIP_DISPLAY_TIME } from '@src/features/shared/community/components/chat/chatList/ChatItemHeader';
import useFileDownload from '@src/hooks/useFileDownload';
import { highlightFilename } from '@src/utils/textHighlight';
import { useState } from 'react';

// エディタ内とチャット内の両方で使えるように
const ChatFileItem = ({
  attachment,
  showTooltip,
  displayTooltip,
  keyword = '',
}: ChatAttachmentItemProps) => {
  const [isActionMenuSpOpen, setIsActionMenuSpOpen] = useState<boolean>(false);
  const [attachmentErrorModalOpen, setAttachmentErrorModalOpen] = useState(false);
  const [attachmentErrorMessage, setAttachmentErrorMessage] = useState('');
  const [isClosing, setIsClosing] = useState(false);
  const { downloadFile } = useFileDownload();
  const { openModal: globalOpenModal, closeModal: globalCloseModal } = useIsModal();
  const { profile } = useSharedAuth();
  const isLoggedIn = profile?.companyProfile?.isLogin ?? profile?.tenantProfile?.isLogin;
  const PRESS_LONG_TIME = 600;
  let touchTimer: NodeJS.Timeout;

  const handleFileLongPress = () => {
    setIsActionMenuSpOpen(true);
    globalOpenModal();
  };

  const handleFileActionMenuClose = () => {
    clearTimeout(touchTimer);
    setIsClosing(true);
    setIsActionMenuSpOpen(false);
    globalCloseModal();

    setTimeout(() => {
      setIsClosing(false);
    }, 300);
  };

  const handleDownloadClick = async () => {
    try {
      if (!isLoggedIn) {
        displayTooltip?.(TOOLTIP_DISPLAY_TIME);
        return;
      }
      await downloadFile(attachment.file_url, attachment.file_name);
    } catch {
      setAttachmentErrorMessage('ダウンロードに失敗しました。再試行してください。');
      setAttachmentErrorModalOpen(true);
    }
  };

  return (
    <div
      className='group/file-download relative flex h-[48px] w-[200px] cursor-pointer select-none items-center gap-[10px] rounded-[4px] border border-solid border-gray-200 p-2'
      style={{
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        userSelect: 'none',
      }}
      onContextMenu={(e) => e.preventDefault()}
      onTouchStart={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.touches.length === 1 && !isActionMenuSpOpen && !isClosing) {
          touchTimer = setTimeout(() => {
            handleFileLongPress();
          }, PRESS_LONG_TIME);
        }
      }}
      onTouchEnd={(e) => {
        e.preventDefault();
        e.stopPropagation();
        clearTimeout(touchTimer);
      }}
      onTouchCancel={(e) => {
        e.preventDefault();
        e.stopPropagation();
        clearTimeout(touchTimer);
        handleFileActionMenuClose();
      }}
    >
      <div className='size-8 shrink-0'>
        <img
          width={32}
          height={32}
          src={fetchIcon(attachment.extension)}
          alt=''
          style={{
            WebkitTouchCallout: 'none',
            WebkitUserSelect: 'none',
            userSelect: 'none',
            pointerEvents: 'none',
          }}
          draggable={false}
          onContextMenu={(e) => e.preventDefault()}
          onTouchStart={(e) => e.preventDefault()}
        />
      </div>
      <div className='min-w-0'>
        <p className='truncate font-semibold leading-normal'>
          {keyword ? highlightFilename(attachment.file_name, keyword) : attachment.file_name}
        </p>
        <p className='leading-none'>{`.${attachment.extension}`}</p>
      </div>
      <button
        className='absolute right-1 top-1/2 hidden size-10 -translate-y-1/2 place-items-center rounded-[4px] bg-white group-hover/file-download:grid md:!hidden'
        onClick={handleDownloadClick}
      >
        <img
          src={IconDownload}
          alt=''
        />
      </button>
      {isActionMenuSpOpen && (
        <AttachmentActionMenuSp
          handleCloseClick={handleFileActionMenuClose}
          handleDownloadClick={() => {
            handleDownloadClick();
          }}
          showTooltip={showTooltip}
          fileUrl={attachment.file_url}
          fileName={attachment.file_name}
        />
      )}
      {attachmentErrorModalOpen && (
        <AttachmentErrorModal
          fileName={attachment.file_name}
          attachmentErrorMessage={attachmentErrorMessage}
          onClose={() => setAttachmentErrorModalOpen(false)}
        />
      )}
    </div>
  );
};

export default ChatFileItem;
